import {
  type AppointmentFilterActions,
  type CalenderAppointmentsSearchFilters,
  type TeamCalendarState,
} from "@/features/calendar/calendarFilter/appointmentFilterReducer";
import type { CalendarFilters, CalendarSettings } from "@repo/api";
import { type RecursivePartial } from "@repo/utils";
import { useEffect, useRef } from "react";

export const useSyncLoadedCalendarSettings = ({
  calendarSettings,
  isLoadingCalendarSettings,
  defaultValues,
  actions,
}: {
  calendarSettings: CalendarSettings;
  isLoadingCalendarSettings: boolean;
  defaultValues?: RecursivePartial<CalenderAppointmentsSearchFilters>;
  actions: AppointmentFilterActions;
}) => {
  const hasAppliedCalendarSettingsRef = useRef(false);

  useEffect(() => {
    if (hasAppliedCalendarSettingsRef.current || isLoadingCalendarSettings) {
      return;
    }
    hasAppliedCalendarSettingsRef.current = true;

    if (defaultValues?.tableView === undefined) {
      actions.setTableView(calendarSettings.preferredCalendarView);
    }

    const urlSelection = defaultValues?.teamCalendar?.selection;
    const teamId =
      (urlSelection?.kind === "team" ? urlSelection.teamId : undefined) ??
      calendarSettings.selectedTeam;
    const teamCalendar: TeamCalendarState = teamId
      ? {
          selection: {
            kind: "team",
            teamId,
            assignedToTeam: urlSelection?.assignedToTeam ?? false,
            deselectedMemberIds:
              calendarSettings.deselectedMembersByTeam?.[teamId] ?? [],
          },
          emailId: [],
        }
      : {
          selection: undefined,
          emailId: (defaultValues?.teamCalendar?.emailId as string[]) ?? [],
        };

    actions.syncLoadedCalendarSettings({
      calendarSettings,
      teamCalendar,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCalendarSettings]);
};

export const useSyncLoadedCalendarFilters = ({
  calendarFilters,
  isLoadingCalendarFilters,
  actions,
}: {
  calendarFilters: CalendarFilters;
  isLoadingCalendarFilters: boolean;
  actions: AppointmentFilterActions;
}) => {
  const hasAppliedCalendarFiltersRef = useRef(false);

  useEffect(() => {
    if (hasAppliedCalendarFiltersRef.current || isLoadingCalendarFilters) {
      return;
    }
    hasAppliedCalendarFiltersRef.current = true;

    actions.syncLoadedCalendarFilters(calendarFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCalendarFilters]);
};
